import { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import Spinner from './Spinner';

const VARIANTS = {
  primary: 'bg-primary text-primary-contrast border border-primary hover:bg-primary/90',
  secondary: 'bg-transparent text-ink border border-ink/25 hover:border-ink hover:bg-ink/[0.03]',
  ghost: 'bg-transparent text-ink border border-transparent hover:bg-ink/5',
  inverse: 'bg-primary-contrast text-primary border border-primary-contrast hover:bg-primary-contrast/90',
};

const SIZES = {
  sm: 'min-h-[40px] px-4 text-sm',
  md: 'min-h-[48px] px-6 text-[15px]',
  lg: 'min-h-[52px] px-8 text-base',
};

/**
 * Button / link-button. Corner shape follows the theme (rounded-btn).
 *  - `to="/path"`  renders a react-router <Link>
 *  - `href="..."`  renders a plain <a> (external links; add target/rel yourself)
 *  - otherwise     renders a <button type="button"> (use type="submit" in forms)
 * Touch target is at least 40px tall (48px from size md).
 */
const Button = forwardRef(function Button(
  {
    to,
    href,
    variant = 'primary',
    size = 'md',
    loading = false,
    disabled = false,
    block = false,
    className = '',
    children,
    ...rest
  },
  ref
) {
  const classes = [
    'inline-flex select-none items-center justify-center gap-2 rounded-btn font-semibold tracking-[0.01em] transition-colors duration-150',
    'disabled:cursor-not-allowed disabled:opacity-45 aria-disabled:cursor-not-allowed aria-disabled:opacity-45',
    VARIANTS[variant] || VARIANTS.primary,
    SIZES[size] || SIZES.md,
    block ? 'w-full' : '',
    className,
  ].join(' ');

  const content = (
    <>
      {loading && <Spinner className="h-4 w-4" />}
      {children}
    </>
  );

  if (to) {
    return (
      <Link ref={ref} to={to} className={classes} {...rest}>
        {content}
      </Link>
    );
  }
  if (href) {
    return (
      <a ref={ref} href={href} className={classes} {...rest}>
        {content}
      </a>
    );
  }
  return (
    <button
      ref={ref}
      type="button"
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {content}
    </button>
  );
});

export default Button;
